/**
 * 画布视口辅助：点击落点、首次 fit、按 phase 切换视口。
 *
 * 根因：多 phase 共用同一 React Flow 实例与视口；切 phase 只换可见节点，
 * 相机仍停在上一 phase 的平移/缩放上，坐标不同的节点会飘出可视区。
 *
 * @author 天马 (Pegasus-Yang) <panesas2@163.com>
 */

/**
 * 取 wrapper 的屏幕中心点（给 screenToFlowPosition 用）。
 * @param {DOMRect|{left:number,top:number,width:number,height:number}|null|undefined} rect
 * @returns {{x:number,y:number}|null}
 */
export function getCanvasCenterScreenPoint(rect) {
  if (!rect) return null;
  const { left, top, width, height } = rect;
  if (!(width > 0) || !(height > 0)) return null;
  return {
    x: left + width / 2,
    y: top + height / 2,
  };
}

/**
 * 是否应执行「有初始节点时的一次性 fitView」。
 * 空画布创建不 fit（点击落在视口中心即可）；重载已有图才 fit。
 *
 * @param {{
 *   hasFitOnce: boolean,
 *   startedWithNodes: boolean,
 *   width: number,
 *   height: number,
 *   nodeCount: number,
 * }} opts
 * @returns {boolean}
 */
export function shouldTryInitialFit(opts) {
  const { hasFitOnce, startedWithNodes, width, height, nodeCount } = opts;
  if (hasFitOnce) return false;
  if (!startedWithNodes) return false;
  if (!(nodeCount > 0)) return false;
  if (!(width > 0) || !(height > 0)) return false;
  return true;
}

/**
 * 画布内节点是否已有非零 DOM 尺寸（自定义 node 测量完成前 offset 常为 0）。
 * React Flow v12 的 fitView 是异步排队的：节点未测量时排队后可能空转，
 * 且本项目过滤了 dimensions change，nodesInitialized 可能长期为 false。
 *
 * @param {ParentNode|null|undefined} wrapperEl
 * @returns {boolean}
 */
export function nodesHaveDomSize(wrapperEl) {
  if (!wrapperEl || typeof wrapperEl.querySelectorAll !== "function") {
    return false;
  }
  const nodeEls = wrapperEl.querySelectorAll(".react-flow__node");
  if (nodeEls.length === 0) return false;
  for (const n of nodeEls) {
    if (!(n.offsetWidth > 0) || !(n.offsetHeight > 0)) return false;
  }
  return true;
}

/**
 * fit 后至少有一个节点与 wrapper 矩形相交（用于确认异步 fitView 真正生效）。
 *
 * @param {Element|null|undefined} wrapperEl
 * @returns {boolean}
 */
export function anyNodeInWrapperView(wrapperEl) {
  if (!wrapperEl || typeof wrapperEl.getBoundingClientRect !== "function") {
    return false;
  }
  const wr = wrapperEl.getBoundingClientRect();
  if (!(wr.width > 0) || !(wr.height > 0)) return false;
  const nodeEls = wrapperEl.querySelectorAll(".react-flow__node");
  for (const n of nodeEls) {
    const r = n.getBoundingClientRect();
    if (
      r.right > wr.left &&
      r.left < wr.right &&
      r.bottom > wr.top &&
      r.top < wr.bottom
    ) {
      return true;
    }
  }
  return false;
}

/**
 * 判断是否为合法的 React Flow 视口快照。
 * @param {unknown} vp
 * @returns {vp is {x:number,y:number,zoom:number}}
 */
export function isViewportSnapshot(vp) {
  if (!vp || typeof vp !== "object") return false;
  const { x, y, zoom } = /** @type {{x?:unknown,y?:unknown,zoom?:unknown}} */ (vp);
  return (
    typeof x === "number" &&
    Number.isFinite(x) &&
    typeof y === "number" &&
    Number.isFinite(y) &&
    typeof zoom === "number" &&
    Number.isFinite(zoom) &&
    zoom > 0
  );
}

/**
 * 切 phase 时决定下一步视口动作（纯函数，便于单测）。
 *
 * - 离开旧 phase：把 currentViewport 写入 map（若合法）
 * - 进入新 phase：有缓存则 restore；否则有可见节点则 fit；空画布 none
 *
 * @param {{
 *   prevPhase: string,
 *   nextPhase: string,
 *   currentViewport: {x:number,y:number,zoom:number}|null|undefined,
 *   savedByPhase: Record<string, {x:number,y:number,zoom:number}>,
 *   nextNodeCount: number,
 * }} opts
 * @returns {{
 *   savedByPhase: Record<string, {x:number,y:number,zoom:number}>,
 *   action: {type: "restore", viewport: {x:number,y:number,zoom:number}} | {type: "fit"} | {type: "none"},
 * }}
 */
export function planPhaseViewportChange(opts) {
  const {
    prevPhase,
    nextPhase,
    currentViewport,
    savedByPhase,
    nextNodeCount,
  } = opts;

  const nextSaved = { ...savedByPhase };
  if (prevPhase !== nextPhase && isViewportSnapshot(currentViewport)) {
    nextSaved[prevPhase] = {
      x: currentViewport.x,
      y: currentViewport.y,
      zoom: currentViewport.zoom,
    };
  }

  if (prevPhase === nextPhase) {
    return { savedByPhase: nextSaved, action: { type: "none" } };
  }

  const cached = nextSaved[nextPhase];
  if (isViewportSnapshot(cached)) {
    return {
      savedByPhase: nextSaved,
      action: { type: "restore", viewport: { ...cached } },
    };
  }

  if (nextNodeCount > 0) {
    return { savedByPhase: nextSaved, action: { type: "fit" } };
  }

  return { savedByPhase: nextSaved, action: { type: "none" } };
}
